export type CoDeSysDataTypeInfo = {
  name: string;
  description: string;
  sizeInBits: string;
  range: string;
  bitLayoutExplanation: string;
  usageExamples: string;
  syntaxExample: string;
  initializationExample: string;
  image: string;
};

export type BacnetObjectInfo = {
  name: string;
  objectTypeIdentifier: string;
  description: string;
  commonProperties: string[];
  typicalServices: string[];
  detailedExplanation: string;
  image: string;
};



export const coDeSysData: CoDeSysDataTypeInfo[] = [
  {
    name: "BOOL",
    description: "Der Datentyp BOOL (Boolean) repräsentiert einen Wahrheitswert. Er kann entweder den Zustand WAHR (TRUE) oder FALSCH (FALSE) annehmen. Er ist einer der fundamentalsten Datentypen in der Logikverarbeitung.",
    sizeInBits: "Implementierungsabhängig, typischerweise 8 Bit (1 Byte) zur einfacheren Adressierung, obwohl nur 1 Bit für die Information benötigt wird.",
    range: "TRUE (entspricht meist 1) oder FALSE (entspricht meist 0).",
    bitLayoutExplanation: "Das niedrigstwertige Bit (Bit 0) des belegten Bytes wird für den Zustand TRUE (1) oder FALSE (0) verwendet. Die restlichen Bits des Bytes sind typischerweise ungenutzt oder undefiniert für den reinen BOOL-Wert.",
    usageExamples: `- Speichern von Zuständen (z.B. Ventil offen/geschlossen, Motor ein/aus)
- Ergebnis von Vergleichen (z.B. bVar := iZahl1 > iZahl2;)
- Bedingungen in IF-, WHILE-, REPEAT-Anweisungen
- Verknüpfung logischer Operationen (AND, OR, NOT, XOR)
- Direkte Adressierung von digitalen Ein- und Ausgängen (z.B. %QX0.0, %IX0.1)`,
    syntaxExample: "VAR\n  bLampeAn : BOOL;\n  bStoerung : BOOL := FALSE;\nEND_VAR",
    initializationExample: "bLampeAn := TRUE;\nIF bSensorAktiv THEN\n  bAlarm := TRUE;\nEND_IF",
    image: "/assets/drawable/datatype_bool.svg"
  },
  {
    name: "INT (Integer)",
    description: "Der Datentyp INT (Integer) repräsentiert eine ganze Zahl mit Vorzeichen. Er wird verwendet, um numerische Werte zu speichern, die sowohl positiv als auch negativ sein können.",
    sizeInBits: "16 Bit (2 Byte).",
    range: "-32.768 bis +32.767.",
    bitLayoutExplanation: `Verwendet die Zweierkomplementdarstellung:
- **Bit 15 (MSB - Most Significant Bit):** Vorzeichenbit. 0 für positive Zahlen, 1 für negative Zahlen.
- **Bits 0-14:** Wertbits.
Beispiel für +5: \`0000 0000 0000 0101\`
Beispiel für -5 (Zweierkomplement von +5):
  1. Invertieren aller Bits von +5: \`1111 1111 1111 1010\`
  2. Addieren von 1: \`1111 1111 1111 1011\` (das ist -5)`,
    usageExamples: `- Zähler (z.B. Stückzähler, Betriebsstundenzähler).
- Speichern von Messwerten, die diskret und ganzzahlig sind (z.B. Anzahl Umdrehungen).
- Indizes für Arrays.
- Arithmetische Berechnungen.
- Vergleichsoperationen.`,
    syntaxExample: `VAR
  iAnzahlTeile : INT;
  iTemperaturVorgabe : INT := 20;
END_VAR`,
    initializationExample: `iAnzahlTeile := iAnzahlTeile + 1;
iFehlercode := -100;`,
    image: "/assets/drawable/datatype_int.svg"
  },
  {
    name: "SINT (Short Integer)",
    description: "Der Datentyp SINT (Short Integer) repräsentiert eine kurze ganze Zahl mit Vorzeichen. Er benötigt weniger Speicherplatz als INT, hat aber einen kleineren Wertebereich.",
    sizeInBits: "8 Bit (1 Byte).",
    range: "-128 bis +127.",
    bitLayoutExplanation: `Verwendet die Zweierkomplementdarstellung:
- **Bit 7 (MSB):** Vorzeichenbit (0 = positiv, 1 = negativ).
- **Bits 0-6:** Wertbits.`,
    usageExamples: `- Wenn der Wertebereich von -128 bis +127 ausreicht und Speicherplatz optimiert werden soll.
- Speichern kleiner Konfigurationswerte oder Zustandsnummern.
- Verarbeitung von Daten von Sensoren, die Werte in diesem Bereich liefern.`,
    syntaxExample: `VAR
  siSensorStatus : SINT;
END_VAR`,
    initializationExample: `siSensorStatus := 10;`,
    image: "/assets/drawable/datatype_int.svg"
  },
  {
    name: "DINT (Double Integer)",
    description: "Der Datentyp DINT (Double Integer) repräsentiert eine doppelt so große ganze Zahl mit Vorzeichen wie INT. Er wird für größere numerische Werte verwendet.",
    sizeInBits: "32 Bit (4 Byte).",
    range: "-2.147.483.648 bis +2.147.483.647.",
    bitLayoutExplanation: `Verwendet die Zweierkomplementdarstellung:
- **Bit 31 (MSB):** Vorzeichenbit.
- **Bits 0-30:** Wertbits.`,
    usageExamples: `- Zähler für große Anzahlen.
- Speichern von hochauflösenden Positionswerten.
- Verarbeitung von Zeitstempeln (oft als Anzahl von Sekunden seit einer Epoche).
- Wenn Berechnungen mit INT zu einem Überlauf führen könnten.`,
    syntaxExample: `VAR
  diGesamtproduktion : DINT;
END_VAR`,
    initializationExample: `diGesamtproduktion := 1250000;`,
    image: "/assets/drawable/datatype_dint.svg"
  },
  {
    name: "LINT (Long Integer)",
    description: "Der Datentyp LINT (Long Integer) repräsentiert eine sehr große ganze Zahl mit Vorzeichen. Er wird für extrem große numerische Werte verwendet.",
    sizeInBits: "64 Bit (8 Byte).",
    range: "-9.223.372.036.854.775.808 bis +9.223.372.036.854.775.807.",
    bitLayoutExplanation: `Verwendet die Zweierkomplementdarstellung:
- **Bit 63 (MSB):** Vorzeichenbit.
- **Bits 0-62:** Wertbits.`,
    usageExamples: `- Verarbeitung von sehr großen Zählwerten oder Akkumulationen.
- Zeitmessung in Nanosekunden oder Mikrosekunden über lange Zeiträume.
- Kryptographische Anwendungen oder komplexe mathematische Berechnungen, die diesen Wertebereich erfordern.`,
    syntaxExample: `VAR
  liNanosekunden : LINT;
END_VAR`,
    initializationExample: `liNanosekunden := T_TO_LINT(TIME#1S) * 1000000000;`,
    image: "/assets/drawable/datatype_dint.svg"
  },
  {
    name: "UINT (Unsigned Integer)",
    description: "Der Datentyp UINT (Unsigned Integer) repräsentiert eine ganze Zahl ohne Vorzeichen. Er kann nur positive Werte (einschließlich Null) speichern, dafür aber einen doppelt so großen positiven Wertebereich wie INT abdecken.",
    sizeInBits: "16 Bit (2 Byte).",
    range: "0 bis 65.535.",
    bitLayoutExplanation: `Alle 16 Bits werden zur Darstellung des Werts verwendet. Es gibt kein separates Vorzeichenbit. Der Wert wird direkt binär interpretiert.`,
    usageExamples: `- Wenn nur positive Werte erwartet werden und der Wertebereich von INT nicht ausreicht (z.B. Adressierung, Hardware-Register).
- Speichern von Daten, die naturgemäß keine negativen Werte annehmen können (z.B. Längen, Anzahlen).
- Bitmaskierung und bitweise Operationen auf 16-Bit-Werten.`,
    syntaxExample: `VAR
  uiRegisterWert : UINT;
  uiZaehlerPositiv : UINT := 0;
END_VAR`,
    initializationExample: `uiRegisterWert := 16#FFFF; (* Entspricht 65535 dezimal *)`,
    image: "/assets/drawable/datatype_uint.svg"
  },
  {
    name: "USINT (Unsigned Short Integer)",
    description: "Der Datentyp USINT (Unsigned Short Integer) repräsentiert eine kurze ganze Zahl ohne Vorzeichen. Er benötigt nur 8 Bit Speicherplatz und wird für kleine positive Zahlenwerte verwendet.",
    sizeInBits: "8 Bit (1 Byte).",
    range: "0 bis 255.",
    bitLayoutExplanation: `Alle 8 Bits werden zur Darstellung des Werts verwendet. Es gibt kein Vorzeichenbit. Der Wert wird direkt binär interpretiert.`,
    usageExamples: `- Speichern von kleinen, nicht-negativen Werten (z.B. Zähler bis 255, Statuscodes).
- Repräsentation von Bytewerten oder ASCII-Zeichen (wenn als numerischer Wert interpretiert).
- Effiziente Speicherung, wenn der Wertebereich ausreicht.
- Bitweise Operationen auf 8-Bit-Werten.`,
    syntaxExample: `VAR
  usiKleinZahl : USINT;
  usiStatusByte : USINT := 16#0A; (* 10 dezimal *)
END_VAR`,
    initializationExample: `usiKleinZahl := 128;
usiStatusByte := usiStatusByte OR 16#01;`,
    image: "/assets/drawable/datatype_usint.svg"
  },
  {
    name: "UDINT (Unsigned Double Integer)",
    description: "Der Datentyp UDINT (Unsigned Double Integer) repräsentiert eine 32-Bit ganze Zahl ohne Vorzeichen. Er wird für größere positive numerische Werte verwendet, die den Bereich von UINT übersteigen.",
    sizeInBits: "32 Bit (4 Byte).",
    range: "0 bis 4.294.967.295.",
    bitLayoutExplanation: `Alle 32 Bits werden zur Darstellung des Werts verwendet, ohne Vorzeichenbit.`,
    usageExamples: `- Zähler für große Anzahlen, die nicht negativ werden können.
- Speichern von Zeitstempeln oder Zeitdifferenzen in Millisekunden (wenn der Wertebereich passt).
- Adressierung oder eindeutige Identifikatoren, die einen großen positiven Bereich benötigen.
- Bitmasken und bitweise Operationen auf 32-Bit-Werten.
- Ergebnis von Berechnungen, bei denen ein Überlauf eines UINT vermieden werden soll.`,
    syntaxExample: `VAR
  udiGrosserZaehler : UDINT;
  udiTimerWertMs : UDINT;
END_VAR`,
    initializationExample: `udiGrosserZaehler := 1000000;
udiTimerWertMs := DWORD_TO_UDINT(SysTimeGetMs());`,
    image: "/assets/drawable/datatype_uint.svg"
  },
  {
    name: "ULINT (Unsigned Long Integer)",
    description: "Der Datentyp ULINT (Unsigned Long Integer) repräsentiert eine sehr große 64-Bit ganze Zahl ohne Vorzeichen. Er wird für extrem große positive numerische Werte verwendet.",
    sizeInBits: "64 Bit (8 Byte).",
    range: "0 bis 18.446.744.073.709.551.615.",
    bitLayoutExplanation: `Alle 64 Bits werden zur Darstellung des Werts verwendet, ohne Vorzeichenbit.`,
    usageExamples: `- Verarbeitung von sehr großen positiven Zählwerten, die den Bereich von UDINT sprengen.
- Zeitmessung in Nanosekunden oder Mikrosekunden über lange Zeiträume ohne Überlauf.
- Speicherung von Dateigrößen oder großen Speicheradressen (abhängig vom System).
- Anwendungen in der Kryptographie oder bei komplexen Berechnungen, die diesen riesigen positiven Wertebereich erfordern.`,
    syntaxExample: `VAR
  uliSehrGross : ULINT;
  uliNanosekundenCounter : ULINT;
END_VAR`,
    initializationExample: `uliSehrGross := 10_000_000_000_000_000_000; (* Beispielwert *)
uliNanosekundenCounter := LWORD_TO_ULINT(SysTimeGetNs());`,
    image: "/assets/drawable/datatype_uint.svg"
  },
  {
    name: "REAL",
    description: "Der Datentyp REAL repräsentiert eine Gleitkommazahl mit einfacher Genauigkeit gemäß dem IEEE 754 Standard. Er wird verwendet, um Zahlen mit Nachkommastellen darzustellen.",
    sizeInBits: "32 Bit (4 Byte).",
    range: "Ca. ±1.18e-38 bis ±3.40e+38. Kann auch spezielle Werte wie NaN (Not a Number) und +/- Infinity darstellen.",
    bitLayoutExplanation: `Nach IEEE 754 Standard (single-precision):
- **Bit 31:** Vorzeichenbit (S) (0 = positiv, 1 = negativ).
- **Bits 30-23 (8 Bit):** Exponent (E). Der Exponent wird mit einem Bias von 127 gespeichert (d.h. der gespeicherte Wert ist E + 127).
- **Bits 22-0 (23 Bit):** Mantisse (M) oder Signifikand. Repräsentiert den gebrochenen Teil der Zahl. Es gibt ein implizites führendes Bit (normalerweise 1), das nicht gespeichert wird (außer bei subnormalen Zahlen).
- Wert = (-1)^S * (1.M) * 2^(E-127) (für normalisierte Werte)`,
    usageExamples: `- Speichern von Messwerten mit Nachkommastellen (z.B. Temperatur, Druck, Geschwindigkeit).
- Ergebnis von Divisionen oder mathematischen Funktionen, die nicht-ganzzahlige Ergebnisse liefern.
- Sollwerte für Regelungen, die eine feine Abstufung erfordern.
- Berechnungen in der Physik, Technik und Wissenschaft.
- **Vorsicht:** Begrenzte Genauigkeit. Bei vielen aufeinanderfolgenden Berechnungen können sich Rundungsfehler akkumulieren. Für höhere Genauigkeit LREAL verwenden.`,
    syntaxExample: `VAR
  rTemperatur : REAL;
  rSollwertDruck : REAL := 1.25;
END_VAR`,
    initializationExample: `rTemperatur := 23.75;
rErgebnis := SIN(rWinkelRad);`,
    image: "/assets/drawable/datatype_real.svg"
  },
  {
    name: "STRING",
    description: "Der Datentyp STRING repräsentiert eine Zeichenkette variabler Länge. Er speichert eine Sequenz von Zeichen gemäß dem ASCII-Standard (oder genauer: gemäß der aktuell im System eingestellten Einzelbyte-Zeichenkodierung, oft ANSI). Die maximale Länge kann bei der Deklaration angegeben werden.",
    sizeInBits: "Abhängig von der deklarierten Länge. Jedes Zeichen belegt 1 Byte (8 Bit). Zusätzlich wird Speicher für die Längeninformation benötigt (oft 1 Byte für die aktuelle Länge). Beispiel: STRING(80) benötigt 80 Bytes für Zeichen + 1 Byte für Länge = 81 Bytes.",
    range: "Eine Sequenz von 0 bis zur deklarierten maximalen Anzahl von Zeichen (Standard oft 80, wenn keine Länge angegeben wird, Maximum ist implementierungsabhängig, z.B. 255 oder höher).",
    bitLayoutExplanation: `- **Längen-Byte (meist das erste Byte):** Speichert die aktuelle Anzahl der gültigen Zeichen im String (0 bis max. Länge).
- **Zeichen-Bytes:** Jedes nachfolgende Byte speichert ein Zeichen gemäß der Einzelbyte-Kodierung (z.B. ASCII, ISO-8859-1).
- **Terminierung:** Im Gegensatz zu C-Strings wird in CoDeSys ein String typischerweise *nicht* mit einem Nullzeichen abgeschlossen, sondern die Länge wird explizit gespeichert. Einige Implementierungen können intern dennoch mit Nullterminierung arbeiten, insbesondere bei der Interaktion mit externen Bibliotheken.`,
    usageExamples: `- Speichern und Verarbeiten von Textinformationen (z.B. Meldungen, Dateinamen, Benutzereingaben).
- Erstellen von dynamischen Textausgaben für Visualisierungen oder Protokolle.
- Vergleichen von Zeichenketten.
- Konkatenation (Zusammenfügen) von Strings.
- Nutzung von String-Funktionen aus der Standardbibliothek (z.B. LEN, LEFT, RIGHT, MID, CONCAT, FIND, REPLACE, DELETE, INSERT).`,
    syntaxExample: `VAR
  sMeldung : STRING; (* Standardlänge, z.B. 80 *)
  sBedienerName : STRING(30) := 'Max Mustermann';
  sLeer : STRING := '';
END_VAR`,
    initializationExample: `sMeldung := 'Störung an Anlage 5';
sPfad := CONCAT(sBasisPfad, sDateiName);`,
    image: "/assets/drawable/datatype_string.svg"
  },
  {
    name: "TIME (T)",
    description: "Der Datentyp TIME (oder kurz T) repräsentiert eine Zeitdauer. Er wird verwendet, um Zeitintervalle zu speichern und für Zeitsteuerungen (Timer) oder Zeitmessungen.",
    sizeInBits: "Typischerweise 32 Bit (4 Byte) als vorzeichenlose Ganzzahl (UDINT), die Millisekunden darstellt. Kann je nach Zielsystem auch 64 Bit (LWORD/ULINT) für LTIME sein für höhere Auflösung oder längere Dauer.",
    range: "Für 32-Bit: 0 ms bis T#49d17h2m47s295ms (entspricht 2^32 - 1 Millisekunden). Für LTIME (64-Bit) entsprechend größer.",
    bitLayoutExplanation: `Intern wird die Zeitdauer als eine Ganzzahl von Millisekunden gespeichert. Bei der Eingabe oder Anzeige wird das TIME-Literal-Format verwendet (z.B. T#5s300ms).`,
    usageExamples: `- Zeitvorgaben für Timer-Funktionsbausteine (TON, TOF, TP).
- Speichern von Zykluszeiten oder Bearbeitungsdauern.
- Zeitgesteuerte Abläufe.
- Verzögerungen in Programmen.
- Arithmetische Operationen mit Zeitdauern (Addition, Subtraktion).
- Vergleich von Zeitdauern.`,
    syntaxExample: `VAR
  tVerzoegerung : TIME := T#5s;
  tZyklusMax : TIME;
  tImpulsdauer : TIME := T#250ms;
END_VAR`,
    initializationExample: `tVerzoegerung := T#1m10s500ms;
IF TON_Instance.Q THEN
  // Aktion nach Ablauf von TON_Instance.PT
END_IF`,
    image: "/assets/drawable/datatype_time.svg"
  },
  {
    name: "WSTRING",
    description: "Der Datentyp WSTRING (Wide String) repräsentiert eine Zeichenkette variabler Länge, die Breitzeichen (typischerweise Unicode-Zeichen) speichert. Jedes Zeichen belegt mehr als ein Byte (meist 2 Bytes für UCS-2 oder UTF-16). Dies ermöglicht die Darstellung eines größeren Zeichenumfangs, einschließlich internationaler Schriftzeichen.",
    sizeInBits: "Abhängig von der deklarierten Länge. Jedes Zeichen belegt 2 Bytes (16 Bit) für UCS-2/UTF-16. Zusätzlich wird Speicher für die Längeninformation benötigt (oft 2 Bytes für die aktuelle Länge in Zeichen). Beispiel: WSTRING(40) benötigt 40 * 2 Bytes für Zeichen + 2 Bytes für Länge = 82 Bytes.",
    range: "Eine Sequenz von 0 bis zur deklarierten maximalen Anzahl von Breitzeichen (Maximum ist implementierungsabhängig).",
    bitLayoutExplanation: `- **Längen-Information (meist die ersten 2 Bytes):** Speichert die aktuelle Anzahl der gültigen Breitzeichen im String.
- **Zeichen-Daten:** Jedes nachfolgende Paar von Bytes (WORD) speichert ein Breitzeichen (z.B. im UTF-16 Format).
- Im Gegensatz zu C-artigen nullterminierten Strings wird die Länge explizit gespeichert.`,
    usageExamples: `- Verarbeitung von Texten, die internationale Schriftzeichen enthalten (z.B. Chinesisch, Japanisch, Kyrillisch).
- Anwendungen mit mehrsprachiger Unterstützung (Lokalisierung).
- Datenaustausch mit Systemen, die Unicode verwenden.
- Nutzung von speziellen WSTRING-Funktionen aus Bibliotheken, falls verfügbar.`,
    syntaxExample: `VAR
  wsUnicodeText : WSTRING(100);
  wsGruss : WSTRING;
END_VAR`,
    initializationExample: `wsUnicodeText := "Hallo";`,
    image: "/assets/drawable/datatype_string.svg"
  },
  {
    name: "ANY (Generische Datentypen)",
    description: "Der ANY-Datentyp ist kein konkreter Datentyp, sondern ein Platzhalter, der jeden beliebigen Datentyp repräsentieren kann. Er wird hauptsächlich für Ein- und Ausgänge von Funktionsbausteinen oder Funktionen verwendet, um diese flexibler zu gestalten. CoDeSys unterscheidet verschiedene Arten von ANY-Typen, die bestimmte Familien von Datentypen zulassen.",
    sizeInBits: "Nicht direkt anwendbar, da ANY selbst keinen Speicher belegt, sondern nur auf den Speicher des konkreten Datentyps verweist, der zur Laufzeit übergeben wird.",
    range: "Nicht direkt anwendbar. Der Wertebereich hängt vom konkreten Datentyp ab, der der ANY-Variablen zugewiesen wird.",
    bitLayoutExplanation: `ANY-Variablen speichern intern typischerweise einen Pointer auf die eigentlichen Daten und Informationen über den Typ und die Größe dieser Daten.
Gängige ANY-Typen sind:
- **ANY_ELEMENTARY:** Erlaubt jeden elementaren Datentyp (BOOL, INT, REAL, STRING, TIME, etc.).
- **ANY_NUM:** Erlaubt jeden numerischen Datentyp (INT, REAL, etc.).
- **ANY_INT:** Erlaubt jeden ganzzahligen Datentyp (SINT, INT, UINT, DINT, etc.).
- **ANY_REAL:** Erlaubt jeden Gleitkommadatentyp (REAL, LREAL).
- **ANY_STRING:** Erlaubt STRING und WSTRING.
- **ANY_DATE:** Erlaubt DATE, TOD, DT.
- **ANY_BIT:** Erlaubt alle Datentypen, die eine bitweise Adressierung erlauben (BOOL, BYTE, WORD, DWORD, LWORD).
- **ANY (ohne Suffix):** Der allgemeinste Typ, kann jeden beliebigen Datentyp annehmen, einschließlich benutzerdefinierter Strukturen und Arrays.`,
    usageExamples: `- Erstellen von universell einsetzbaren Funktionen/Funktionsbausteinen (z.B. eine Funktion, die den Inhalt einer beliebigen Variable loggen kann).
- Übergabe von Daten unterschiedlichen Typs an denselben Eingang eines Bausteins.
- Pointer-ähnliche Operationen innerhalb der IEC 61131-3 Grenzen (z.B. mit \`ADR\` und Dereferenzierung, erfordert Vorsicht).
- Bibliotheksentwicklung, um generische Helferfunktionen zu erstellen.
- **Wichtig:** Die Verwendung von ANY erfordert sorgfältiges Typhandling im Baustein, um Laufzeitfehler zu vermeiden. Oft werden Funktionen wie \`SIZEOF\` oder Typüberprüfungen (implizit durch die ANY-Variante) verwendet.`,
    syntaxExample: `FUNCTION_BLOCK GenericLogger
VAR_INPUT
  InputValue : ANY; // Kann jeden Datentyp aufnehmen
END_VAR
---
FUNCTION LogValue : BOOL
VAR_INPUT
  DataToLog : ANY_ELEMENTARY;
  Message : STRING;
END_VAR`,
    initializationExample: `(* Innerhalb eines FB mit Input 'InputValue : ANY' *)
IF __ISVALIDREF(InputValue) THEN // Prüfen ob eine gültige Variable übergeben wurde
  CASE Info(InputValue).TypeClass OF
    TYPE_CLASS.TYPE_INT:
      iWert := InputValue; // Implizite Konvertierung wenn Typ passt
      LogInteger(iWert);
    TYPE_CLASS.TYPE_REAL:
      rWert := InputValue;
      LogReal(rWert);
    // ... weitere Fälle
  END_CASE
END_IF`,
    image: "/assets/drawable/datatype_enum.svg"
  },
  {
    name: "BYTE",
    description: "Der Datentyp BYTE repräsentiert eine Gruppe von 8 Bits. Er wird oft verwendet, um einzelne Bytes zu speichern oder zu manipulieren, für bitweise Operationen oder als Basis für andere Datentypen wie SINT oder USINT.",
    sizeInBits: "8 Bit (1 Byte).",
    range: "Als numerischer Wert oft interpretiert wie USINT (0 bis 255) oder als Hexadezimalwert (z.B. 16#00 bis 16#FF).",
    bitLayoutExplanation: "Eine Sequenz von 8 Bits (Bit 0 bis Bit 7). Jedes Bit kann den Wert 0 oder 1 annehmen.",
    usageExamples: "- Direkter Zugriff auf einzelne Bits einer Variable (z.B. `myByte.%X0` für Bit 0).     - Speichern von Rohdaten oder Kommunikationsprotokoll-Bytes.     - Bitmaskierung und logische Operationen (AND, OR, XOR, NOT) auf Byte-Ebene.     - Datenkonvertierungen, z.B. zwischen Zeichen und deren Bytewert.     - Überlagerung mit anderen 8-Bit-Datentypen mittels AT-Deklaration.",
    syntaxExample: "VAR     byDatenpaket : BYTE;     byFlags : BYTE := 2#10101010; END_VAR",
    initializationExample: "byDatenpaket := 16#AF; IF byFlags.%X7 THEN (* Bit 7 prüfen *)     // Aktion END_IF",
    image: "/assets/drawable/datatype_byte.svg"
  },
  {
    name: "WORD",
    description: "Der Datentyp WORD repräsentiert eine Gruppe von 16 Bits (2 Bytes). Er dient als Basis für Datentypen wie INT oder UINT und wird für bitweise Operationen auf 16-Bit-Ebene verwendet.",
    sizeInBits: "16 Bit (2 Byte).",
    range: "Als numerischer Wert oft interpretiert wie UINT (0 bis 65.535) oder als Hexadezimalwert (z.B. 16#0000 bis 16#FFFF).",
    bitLayoutExplanation: `Eine Sequenz von 16 Bits (Bit 0 bis Bit 15).`,
    usageExamples: `- Direkter Zugriff auf einzelne Bits oder Bytes innerhalb des WORDs.
- Speichern von 16-Bit-Registerwerten von Hardware.
- Bitweise Operationen auf 16-Bit-Daten.
- Überlagerung mit INT, UINT oder zwei BYTEs mittels AT-Deklaration.`,
    syntaxExample: `VAR
  wRegister : WORD;
  wMaske : WORD := 16#FF00;
END_VAR`,
    initializationExample: `wRegister := SystemReadWord(AdresseXY);
wRegister := wRegister AND wMaske;`,
    image: "/assets/drawable/datatype_word.svg"
  },
  {
    name: "DWORD (Double Word)",
    description: "Der Datentyp DWORD (Double Word) repräsentiert eine Gruppe von 32 Bits (4 Bytes). Er ist die Basis für Datentypen wie DINT, UDINT oder REAL.",
    sizeInBits: "32 Bit (4 Byte).",
    range: "Als numerischer Wert oft interpretiert wie UDINT (0 bis 4.294.967.295) oder als Hexadezimalwert (z.B. 16#00000000 bis 16#FFFFFFFF).",
    bitLayoutExplanation: `Eine Sequenz von 32 Bits (Bit 0 bis Bit 31).`,
    usageExamples: `- Bitweise Operationen auf 32-Bit-Daten.
- Speichern von IP-Adressen oder Farbwerten (ARGB).
- Überlagerung mit DINT, UDINT, REAL oder zwei WORDs mittels AT-Deklaration.
- Verarbeitung von Zeitstempeln oder großen Bitfeldern.`,
    syntaxExample: `VAR
  dwStatusBits : DWORD;
  dwIpAdresse : DWORD := 16#C0A80101; (* 192.168.1.1 *)
END_VAR`,
    initializationExample: `dwStatusBits.%X31 := TRUE; (* Höchstes Bit setzen *)
Farbe := DWORD_TO_COLOR(dwFarbwert);`,
    image: "/assets/drawable/datatype_dword.svg"
  },
  {
    name: "LWORD (Long Word)",
    description: "Der Datentyp LWORD (Long Word) repräsentiert eine Gruppe von 64 Bits (8 Bytes). Er ist die Basis für Datentypen wie LINT, ULINT oder LREAL.",
    sizeInBits: "64 Bit (8 Byte).",
    range: "Als numerischer Wert oft interpretiert wie ULINT (0 bis 18.446.744.073.709.551.615) oder als Hexadezimalwert (z.B. 16#0...0 bis 16#F...F).",
    bitLayoutExplanation: `Eine Sequenz von 64 Bits (Bit 0 bis Bit 63).`,
    usageExamples: `- Bitweise Operationen auf sehr breiten Datenfeldern.
- Speichern von MAC-Adressen oder sehr großen eindeutigen Identifikatoren.
- Überlagerung mit LINT, ULINT, LREAL oder zwei DWORDs mittels AT-Deklaration.
- Basis für hochpräzise Zeitstempel (z.B. LTIME).`,
    syntaxExample: `VAR
  lwEinmaligerKey : LWORD;
END_VAR`,
    initializationExample: `lwEinmaligerKey := GET_SYSTEM_TIME_NS(); (* Beispiel, Funktion ist systemspezifisch *)`,
    image: "/assets/drawable/datatype_lword.svg"
  },
  {
    name: "POINTER TO <Datentyp>",
    description: "Ein POINTER ist eine Variable, die die Speicheradresse einer anderen Variablen eines bestimmten Datentyps (oder eines Funktionsbausteins) enthält. Pointer ermöglichen den indirekten Zugriff auf Daten und werden für dynamische Speicherverwaltung oder erweiterte Programmiertechniken verwendet.",
    sizeInBits: "Systemabhängig, typischerweise 32 Bit oder 64 Bit, um den Adressraum des Systems abzudecken.",
    range: "Gültige Speicheradressen des Zielsystems. Ein Pointer kann auch den Wert 0 (NULL-Pointer) haben, was bedeutet, dass er auf keine gültige Adresse zeigt.",
    bitLayoutExplanation: `Der Bit-Inhalt repräsentiert direkt eine Speicheradresse. Die Interpretation dieser Bits ist systemintern.`,
    usageExamples: `- Übergabe von Variablen an Funktionen/Funktionsbausteine 'by reference' (obwohl REFERENCE hierfür oft sicherer ist).
- Dynamische Erstellung von Datenstrukturen (z.B. verkettete Listen), oft in Verbindung mit Operatoren wie \`__NEW\` und \`__DELETE\` (erfordert Vorsicht und ist nicht in allen CoDeSys-Profilen verfügbar/empfohlen).
- Zugriff auf Speicherbereiche, die über Adressen bekannt sind (z.B. Hardware-Register, erfordert extreme Vorsicht).
- Implementierung von Callback-Mechanismen (über Funktionspointer).
- **WICHTIG:** Die Verwendung von Pointern ist mächtig, aber fehleranfällig (ungültige Adressen, Dangling Pointers). Es wird empfohlen, sie nur zu verwenden, wenn es keine sichereren Alternativen (wie REFERENCE TO) gibt. Immer auf Gültigkeit prüfen (Pointer <> 0).`,
    syntaxExample: `VAR
  pIntValue : POINTER TO INT;
  pFB_Instance : POINTER TO FunktionsBlockTyp;
  pBuffer : POINTER TO BYTE;
END_VAR`,
    initializationExample: `VAR
  iMeineZahl : INT := 10;
  pMeineZahl : POINTER TO INT;
END_VAR

pMeineZahl := ADR(iMeineZahl); // Adresse von iMeineZahl zuweisen
IF pMeineZahl <> 0 THEN
  pMeineZahl^ := 20; // Dereferenzieren: Wert, auf den pMeineZahl zeigt, auf 20 setzen
END_IF`,
    image: "/assets/drawable/datatype_pointer.svg"
  },
  {
    name: "REFERENCE TO <Datentyp>",
    description: "Eine REFERENCE ist eine Variable, die als Alias für eine andere Variable eines bestimmten Datentyps oder eines Funktionsbausteins dient. Im Gegensatz zu einem POINTER speichert sie nicht die Speicheradresse, sondern verhält sich wie die Originalvariable selbst. Referenzen bieten einen sicheren 'Call-by-Reference'-Mechanismus und können nicht den Wert 0 (NULL) annehmen oder auf ungültige Speicherbereiche zeigen, nachdem sie initialisiert wurden.",
    sizeInBits: "Systemabhängig, typischerweise die Größe eines Pointers (32 oder 64 Bit), da sie intern oft als Pointer implementiert sind, aber mit zusätzlichen Sicherheitsprüfungen.",
    range: "Muss bei der Deklaration oder spätestens bei der ersten Zuweisung mit einer gültigen Variablen des passenden Typs initialisiert werden. Eine einmal initialisierte Referenz kann nicht mehr auf eine andere Variable umgebogen werden (in Standard IEC 61131-3).",
    bitLayoutExplanation: `Die interne Repräsentation ist für den Programmierer meist nicht direkt relevant, da mit der Referenz wie mit der Originalvariablen gearbeitet wird. Das System stellt sicher, dass die Referenz immer auf eine gültige Variable zeigt, sobald sie zugewiesen ist.`,
    usageExamples: `- Sichere Übergabe von großen Datenstrukturen oder Funktionsbaustein-Instanzen an Funktionen oder Funktionsbausteine, um Kopiervorgänge zu vermeiden (Call-by-Reference).
- Ermöglicht Modifikationen der Originaldaten innerhalb der aufgerufenen POU.
- Sicherere Alternative zu Pointern für viele Anwendungsfälle, da keine explizite Dereferenzierung nötig ist und keine Prüfung auf NULL-Pointer erforderlich ist, nachdem die Referenz gültig zugewiesen wurde.
- **Wichtig:** Eine Referenz muss initialisiert werden, bevor sie verwendet wird. Eine nicht-initialisierte Referenz zu verwenden führt zu einem Laufzeitfehler.`,
    syntaxExample: `VAR
  refIntValue : REFERENCE TO INT;
  refFB_Instance : REFERENCE TO FunktionsBlockTyp;
  iOriginal : INT := 10;
END_VAR`,
    initializationExample: `VAR
  iMeineZahl : INT := 100;
  refMeineZahl : REFERENCE TO INT;
  fbMotor1 : FB_Motor;
  refMotor : REFERENCE TO FB_Motor;
END_VAR

refMeineZahl REF= iMeineZahl; // Zuweisung der Referenz
refMeineZahl := 200; // Ändert iMeineZahl auf 200

refMotor REF= fbMotor1;
refMotor.Start(); // Ruft die Methode Start() auf fbMotor1 auf`,
    image: "/assets/drawable/datatype_pointer.svg"
  },
  {
    name: "ARRAY[...] OF <Datentyp>",
    description: "Ein ARRAY ist eine strukturierte Variable, die eine feste Anzahl von Elementen desselben Datentyps enthält. Der Zugriff auf einzelne Elemente erfolgt über einen oder mehrere ganzzahlige Indizes.",
    sizeInBits: "Gesamtgröße = (Anzahl der Elemente) * (Größe des Basistyps in Bits). Die Indexgrenzen müssen bei der Deklaration festgelegt werden.",
    range: "Die Indizes müssen innerhalb der deklarierten Grenzen liegen. Jedes Element hat den Wertebereich seines Basistyps.",
    bitLayoutExplanation: `Die Elemente des Arrays werden sequenziell im Speicher abgelegt. Die Adresse eines spezifischen Elements kann durch (Basisadresse_Array + Index * Größe_Element) berechnet werden (vereinfacht für eindimensionale Arrays).`,
    usageExamples: `- Speichern von Listen gleichartiger Daten (z.B. Messwertreihen, Konfigurationsparameter, Zustände mehrerer gleicher Geräte).
- Iterative Verarbeitung von Daten mit Schleifen (FOR, WHILE).
- Implementierung von Puffern oder Tabellen.
- Mehrdimensionale Arrays können für Matrizen oder räumliche Datenstrukturen verwendet werden.
- **Wichtig:** Indexüberschreitungen (Zugriff außerhalb der Array-Grenzen) können zu Laufzeitfehlern führen, wenn die Systemoption 'Grenzen prüfen' aktiv ist (empfohlen).`,
    syntaxExample: `VAR
  arrTemperaturen : ARRAY[1..10] OF REAL; (* 10 REAL-Werte, Indizes 1 bis 10 *)
  arrStatusBytes : ARRAY[0..3, 0..7] OF BOOL; (* 2-dimensional, 4x8 BOOL-Werte *)
  arrMeldungen : ARRAY[1..5] OF STRING(80);
END_VAR`,
    initializationExample: `arrTemperaturen[1] := 25.5;
FOR i := 0 TO 3 DO
  FOR j := 0 TO 7 DO
    arrStatusBytes[i,j] := FALSE;
  END_FOR
END_FOR

(* Initialisierung bei Deklaration *)
VAR
  arrKonstanten : ARRAY[1..3] OF INT := [10, 20, 30];
END_VAR`,
    image: "/assets/drawable/datatype_array.svg"
  },
  {
    name: "STRUCT (Struktur)",
    description: "Eine STRUKTUR (STRUCT) ist ein benutzerdefinierter Datentyp, der mehrere Komponenten (Elemente) unterschiedlicher Datentypen unter einem gemeinsamen Namen zusammenfasst. Jede Komponente wird über ihren Namen angesprochen.",
    sizeInBits: "Die Gesamtgröße ist die Summe der Größen aller ihrer Komponenten. Die Anordnung im Speicher (Padding/Alignment) kann systemabhängig sein.",
    range: "Nicht direkt anwendbar. Jede Komponente hat ihren eigenen Wertebereich entsprechend ihres Datentyps.",
    bitLayoutExplanation: "Die Komponenten einer Struktur werden im Speicher sequenziell abgelegt, in der Reihenfolge ihrer Deklaration.     Beispiel:     TYPE ST_SensorData :     STRUCT         bAktiv : BOOL;       (* 1 Byte, je nach System *)         iMesswert : INT;    (* 2 Bytes *)         rTemperatur : REAL; (* 4 Bytes *)     END_STRUCT     END_TYPE     Die Gesamtgröße wäre hier mindestens 1 + 2 + 4 = 7 Bytes. Abhängig von Alignment-Regeln des Zielsystems könnten Füllbytes (Padding) eingefügt werden, um z.B. INT- oder REAL-Werte an geraden Adressen auszurichten, was die Gesamtgröße erhöhen kann.",
    usageExamples: "- Gruppierung logisch zusammengehöriger Daten (z.B. alle Parameter eines Motors, Sensordaten, Rezeptdaten).     - Erstellung komplexer Datenmodelle.     - Verbesserung der Lesbarkeit und Organisation des Programmcodes.     - Übergabe von komplexen Datensätzen an Funktionen oder Funktionsbausteine.     - Zugriff auf Komponenten über den Punktoperator (z.B. `MeineStruktur.Komponente`).     - Strukturen können auch Arrays oder andere Strukturen als Komponenten enthalten.",
    syntaxExample: "TYPE ST_Point : STRUCT     X : INT;     Y : INT;     Farbe : STRING(20); END_STRUCT END_TYPE  VAR     Punkt1 : ST_Point;     RoboterPosition : ST_Point := (X := 100, Y := 250, Farbe := 'Rot');     arrPunkte : ARRAY[1..5] OF ST_Point; END_VAR",
    initializationExample: "Punkt1.X := 50; Punkt1.Y := -20; Punkt1.Farbe := 'Blau'; arrPunkte[1].X := Punkt1.X + 10;",
    image: "/assets/drawable/datatype_struct.svg"
  },
  {
    name: "ENUMERATION (ENUM)",
    description: "Eine ENUMERATION (ENUM) ist ein benutzerdefinierter Datentyp, der eine feste Menge von benannten ganzzahligen Konstanten definiert, die als Enumerationswerte bezeichnet werden. Dies verbessert die Lesbarkeit und Wartbarkeit des Codes, da anstelle von 'magischen Zahlen' sprechende Bezeichner verwendet werden können.",
    sizeInBits: "Typischerweise die Größe eines INT (16 Bit) oder des kleinsten ganzzahligen Typs, der alle Enumerationswerte aufnehmen kann (z.B. BYTE, SINT, UINT, DINT). Der Basisdatentyp kann oft explizit angegeben werden.",
    range: "Die definierten Enumerationswerte. Intern werden diese als Ganzzahlen gespeichert (standardmäßig beginnend bei 0 und für jeden weiteren Wert um 1 erhöht, kann aber explizit zugewiesen werden).",
    bitLayoutExplanation: "Jeder Enumerationsbezeichner wird intern auf einen eindeutigen ganzzahligen Wert abgebildet. Der Speicherbedarf entspricht dem des zugrundeliegenden Ganzzahltyps.",
    usageExamples: "- Darstellung von Zuständen einer Maschine oder eines Prozesses (z.B. AUS, BEREIT, AUTOMATIK, STOERUNG).     - Auswahlmöglichkeiten für Konfigurationen.     - Erhöhung der Codeverständlichkeit, da symbolische Namen anstelle von numerischen Werten verwendet werden.     - Verwendung in CASE-Anweisungen zur Zustandsbehandlung.     - Typsicherheit, da eine ENUM-Variable nur Werte aus ihrer Definition annehmen kann (wird vom Compiler geprüft).",
    syntaxExample: "TYPE E_MaschinenZustand : (     AUS,              (* Standardwert 0 *)     BEREIT,           (* Standardwert 1 *)     AUTOMATIK := 10,  (* Expliziter Wert 10 *)     MANUELL,          (* Wert 11 *)     STOERUNG := 99    (* Wert 99 *) ) // Optional: : INT ; (* Expliziter Basistyp *) END_TYPE  VAR     AktuellerZustand : E_MaschinenZustand; END_VAR",
    initializationExample: "AktuellerZustand := E_MaschinenZustand.BEREIT; CASE AktuellerZustand OF     E_MaschinenZustand.AUS:         // Aktionen für AUS     E_MaschinenZustand.AUTOMATIK:         // Aktionen für AUTOMATIK     ELSE         // Andere Zustände END_CASE",
    image: "/assets/drawable/datatype_enum.svg"
  },
  {
    name: "Implizite Enumeration (Konstantensammlung)",
    description: "Eine implizite Enumeration entsteht, wenn eine Menge benannter Konstanten global oder innerhalb einer Bibliothek definiert wird, die semantisch zusammengehören und Zustände oder Optionen repräsentieren, ohne explizit einen ENUM-Datentyp zu deklarieren. Variablen, die diese Werte annehmen, sind oft vom Typ INT oder einem anderen passenden Ganzzahltyp.",
    sizeInBits: "Abhängig vom Datentyp der Konstanten (meist INT, also 16 Bit, oder der kleinste passende Ganzzahltyp).",
    range: "Die Werte der definierten Konstanten.",
    bitLayoutExplanation: "Die Konstanten sind einfache Ganzzahlwerte. Manchmal werden sie in Bibliotheken so definiert, dass sie bitweise exklusiv sind, um als Flags verwendet zu werden (z.B. 1, 2, 4, 8...).",
    usageExamples: "- Fehlercodes, die von Bibliotheksfunktionen zurückgegeben werden (z.B. `LIB_ERR_NONE`, `LIB_ERR_TIMEOUT`).     - Optionen oder Modi für Bibliotheksfunktionen.     - Status-Flags, die bitweise kombiniert werden können.     - **Unterschied zu explizitem ENUM:** Weniger Typsicherheit durch den Compiler, da eine INT-Variable theoretisch jeden INT-Wert annehmen kann, nicht nur die definierten Konstanten.     - Das Konzept `UNION ALL OF <Enumeration>` (wie im Link beschrieben) ist eine spezielle Art, wie alle Werte einer *expliziten* Enumeration in einer UNION verfügbar gemacht werden können, was aber nicht die typische 'implizite Enumeration' darstellt, die oft durch reine Konstanten-Sammlungen entsteht.",
    syntaxExample: "(* Globale Konstanten oder in einer GVL *) VAR_GLOBAL CONSTANT     MODE_OFF : INT := 0;     MODE_MANUAL : INT := 1;     MODE_AUTO : INT := 2; END_VAR  VAR     iBetriebsart : INT; END_VAR",
    initializationExample: "iBetriebsart := MODE_AUTO; IF iBetriebsart = MODE_MANUAL THEN     // Logik für manuellen Modus END_IF",
    image: "/assets/drawable/datatype_enum.svg"
  },
  {
    name: "TYPE (Typ-Alias)",
    description: "Mit der TYPE-Deklaration kann ein neuer, benutzerdefinierter Datentypname (Alias) für einen bereits existierenden elementaren oder benutzerdefinierten Datentyp erstellt werden. Der neue Typ ist vollständig äquivalent zum Originaltyp, dient aber der besseren Lesbarkeit, Strukturierung oder zur semantischen Unterscheidung.",
    sizeInBits: "Identisch mit der Größe des Originaldatentyps, auf den der Alias verweist.",
    range: "Identisch mit dem Wertebereich des Originaldatentyps.",
    bitLayoutExplanation: "Keine eigene Bit-Belegung. Der Alias verwendet exakt die gleiche Repräsentation wie der Originaldatentyp.",
    usageExamples: "- Verbesserung der Codeverständlichkeit durch sprechende Namen (z.B. `TYPE T_TemperaturSensor : REAL END_TYPE;`).     - Abstraktion von spezifischen Datentypen. Wenn sich der Basistyp später ändern muss (z.B. von INT zu DINT), muss nur die TYPE-Definition angepasst werden, nicht jede Variablendeklaration.     - Definition von Datentypen für spezifische Zwecke, auch wenn sie auf Standardtypen basieren.     - Kann auch für komplexe Typen wie ARRAYs oder STRUCTs verwendet werden, um deren Definition wiederverwendbar zu machen (obwohl STRUCT und ENUM bereits eigene Typnamen erzeugen).",
    syntaxExample: "(* Alias für einen elementaren Typ *) TYPE T_DruckWert : REAL; END_TYPE  (* Alias für einen Array-Typ *) TYPE T_MesswertReihe : ARRAY[1..100] OF INT; END_TYPE  (* Alias für eine bestehende Struktur *) TYPE ST_Koordinate : ST_Point; (* Wobei ST_Point vorher definiert wurde *) END_TYPE  VAR     DruckSensor1 : T_DruckWert;     AlleTemperaturen : T_MesswertReihe;     StartPunkt : ST_Koordinate; END_VAR",
    initializationExample: "DruckSensor1 := 10.5; // Wie ein REAL AlleTemperaturen[1] := -5; // Wie ein INT im Array",
    image: "/assets/drawable/datatype_enum.svg"
  },
  {
    name: "UNION",
    description: "Eine UNION ist ein benutzerdefinierter Datentyp, der mehrere Komponenten (Elemente) unterschiedlicher Datentypen unter einem gemeinsamen Namen zusammenfasst. Im Gegensatz zu einer STRUKTUR belegen jedoch alle Komponenten einer UNION denselben Speicherbereich. Das bedeutet, zu jedem Zeitpunkt ist nur eine Komponente der UNION gültig bzw. repräsentiert den aktuellen Wert. Die Größe der UNION richtet sich nach der Größe ihrer größten Komponente.",
    sizeInBits: "Die Größe der größten Komponente in der UNION. Alle Komponenten teilen sich denselben Speicherplatz.",
    range: "Nicht direkt anwendbar. Der gültige Wertebereich hängt davon ab, welche Komponente der UNION gerade als aktiv betrachtet wird.",
    bitLayoutExplanation: "Alle Komponenten einer UNION beginnen an derselben Speicheradresse. Wenn ein Wert in eine Komponente geschrieben wird, können die Bits dieses Werts die anderen Komponenten beeinflussen, wenn sie denselben Speicherbereich interpretieren.     Beispiel:     TYPE U_MixedData :     UNION         iValue : INT;      (* 2 Bytes *)         byLow : BYTE;     (* 1 Byte, an derselben Adresse wie das Low-Byte von iValue *)         byHigh : BYTE;    (* 1 Byte, an derselben Adresse wie das High-Byte von iValue, falls AT-Deklaration genutzt oder systemabhängig *)         wValue : WORD;    (* 2 Bytes, an derselben Adresse wie iValue *)     END_UNION     END_TYPE     Wenn `myUnion.iValue` geschrieben wird, ändert sich auch der Wert, der als `myUnion.wValue` gelesen wird.",
    usageExamples: "- Speichersparende Darstellung von Daten, bei denen zu einem Zeitpunkt nur eine von mehreren möglichen Interpretationen relevant ist.     - Zugriff auf verschiedene Teile eines größeren Datentyps (z.B. Low- und High-Byte eines WORDs oder INTs), oft in Kombination mit der `AT`-Deklaration für exakte Überlagerung innerhalb der Union (obwohl `AT` auch direkt in Strukturen verwendet werden kann).     - Empfang von Datenpaketen, deren Inhalt je nach einem Typfeld unterschiedlich interpretiert werden muss.     - **Wichtig:** Es liegt in der Verantwortung des Programmierers sicherzustellen, welche Komponente der UNION gerade gültig ist. Das Lesen einer Komponente, die nicht der zuletzt geschriebenen oder beabsichtigten entspricht, führt zu undefinierten oder falsch interpretierten Werten.",
    syntaxExample: "TYPE U_SensorWert : UNION     rAlsReal : REAL;     dwAlsDWord : DWORD;     iAlsIntArray : ARRAY[0..1] OF INT; END_UNION END_TYPE  VAR     Messwert : U_SensorWert;     bRealGesendet : BOOL; END_VAR",
    initializationExample: "IF bRealGesendet THEN     Messwert.rAlsReal := 123.45; ELSE     Messwert.dwAlsDWord := 16#FFFF0000; END_IF  // Späterer Zugriff (Vorsicht!): IF bRealGesendet THEN     rAktuell := Messwert.rAlsReal; END_IF",
    image: "/assets/drawable/datatype_struct.svg"
  },
  {
    name: "Subrange (Teilbereich)",
    description: "Ein Teilbereichsdatentyp (Subrange) ist ein benutzerdefinierter Datentyp, der einen eingeschränkten Wertebereich eines zugrundeliegenden Ganzzahltyps (Basistyp wie SINT, INT, UINT, etc.) definiert. Variablen dieses Typs können nur Werte innerhalb der spezifizierten Untergrenze und Obergrenze annehmen.",
    sizeInBits: "Identisch mit der Größe des Basistyps. Ein Subrange von INT belegt z.B. weiterhin 16 Bit, auch wenn der erlaubte Bereich kleiner ist.",
    range: "Der bei der Deklaration definierte Bereich (Untergrenze .. Obergrenze).",
    bitLayoutExplanation: "Die Bit-Belegung ist identisch mit der des Basistyps. Die Einschränkung des Wertebereichs ist eine logische Definition, die vom Compiler und ggf. zur Laufzeit überwacht wird.",
    usageExamples: "- Erhöhung der Typsicherheit und Robustheit des Programms, da der Compiler (und optional das Laufzeitsystem) prüft, ob zugewiesene Werte im gültigen Bereich liegen.     - Klarere Definition der erlaubten Werte für eine Variable (z.B. ein Monat (1..12), ein Prozentsatz (0..100)).     - Indexvariablen für Arrays, deren Werte auf die gültigen Array-Grenzen beschränkt sind.     - Vermeidung von 'magischen Zahlen' durch semantisch benannte Typen mit klaren Grenzen.     - Wenn die Option 'Grenzen prüfen' im CoDeSys-Projekt aktiviert ist, wird bei einer Verletzung des Wertebereichs zur Laufzeit ein Fehler ausgelöst.",
    syntaxExample: "TYPE T_Prozent : (0..100); (* Basistyp ist der kleinste passende Ganzzahltyp, z.B. USINT oder SINT *) END_TYPE  TYPE T_Monat : INT(1..12); (* Expliziter Basistyp INT *) END_TYPE  TYPE T_ArrayIndex : UINT(0..99); END_TYPE  VAR     Füllstand : T_Prozent;     AktuellerMonat : T_Monat;     Index : T_ArrayIndex; END_VAR",
    initializationExample: "Füllstand := 75; AktuellerMonat := 12; Index := 0;  (* Folgendes würde einen Fehler verursachen, wenn Grenzenprüfung aktiv ist: *) (* Füllstand := 101; *)",
    image: "/assets/drawable/datatype_enum.svg"
  },
  {
    name: "Vektor (ARRAY 1D)",
    description: "In CoDeSys und der IEC 61131-3 ist ein 'Vektor' in der Regel ein eindimensionales ARRAY. Er repräsentiert eine geordnete Sammlung von Elementen desselben Datentyps. Der Begriff wird oft im mathematischen oder physikalischen Kontext verwendet (z.B. Vektor mit X, Y, Z Koordinaten). Es gibt kein spezifisches Schlüsselwort 'VECTOR' für die Deklaration; stattdessen wird 'ARRAY[Indexbereich] OF Datentyp' verwendet.",
    sizeInBits: "Abhängig von der Anzahl der Elemente und der Größe des Basistyps des Arrays. Siehe Datentyp 'ARRAY'.",
    range: "Die Indizes müssen innerhalb der deklarierten Grenzen des Arrays liegen. Jedes Element hat den Wertebereich seines Basistyps. Siehe Datentyp 'ARRAY'.",
    bitLayoutExplanation: "Elemente werden sequenziell im Speicher abgelegt. Siehe Datentyp 'ARRAY'.",
    usageExamples: "- Darstellung von mathematischen Vektoren (z.B. `VAR vec3D : ARRAY[1..3] OF REAL; END_VAR` für einen 3D-Vektor).     - Speichern von Sequenzen oder Listen von Daten.     - Signalverarbeitung, wo Datenpunkte als Vektor auftreten.     - **Spezielle Bibliotheken:** Einige CoDeSys-Bibliotheken (z.B. für Robotik oder Motion Control) könnten eigene Datentypen (DUTs, oft Strukturen oder Aliase von Arrays) namens 'Vector' oder ähnlich definieren, die spezifische Operationen (Vektoraddition, Skalarprodukt etc.) über Methoden oder Funktionen bereitstellen.     - Für einfache Vektoren ist die Standard-ARRAY-Deklaration üblich.",
    syntaxExample: "(* Als eindimensionales Array *) TYPE T_Position2D_Vec : ARRAY[1..2] OF INT; (* Index 1 für X, Index 2 für Y *) END_TYPE  VAR     Geschwindigkeitsvektor : ARRAY[1..3] OF LREAL; (* vx, vy, vz *)     PunktImRaum : T_Position2D_Vec; END_VAR",
    initializationExample: "Geschwindigkeitsvektor[1] := 10.5; // X-Komponente Geschwindigkeitsvektor[2] := -2.3; // Y-Komponente Geschwindigkeitsvektor[3] := 0.0;  // Z-Komponente  PunktImRaum[1] := 100; PunktImRaum[2] := 200;",
    image: "/assets/drawable/datatype_array.svg"
  },
  {
    name: "BIT (Bit-Zugriff)",
    description: "Der Datentyp BIT selbst ist in der IEC 61131-3 nicht direkt zur Deklaration einzelner Variablen vorgesehen (dafür wird BOOL verwendet). 'BIT' bezieht sich auf die kleinste adressierbare Speichereinheit (0 oder 1) und den direkten Zugriff auf einzelne Bits innerhalb von Ganzzahl-Datentypen (BYTE, WORD, DWORD, LWORD) oder in Bitstring-Literalen.",
    sizeInBits: "1 Bit.",
    range: "0 oder 1 (entsprechend FALSE oder TRUE).",
    bitLayoutExplanation: "Ein einzelnes Bit kann entweder den Zustand 0 oder 1 annehmen.",
    usageExamples: "- **Direkter Bitzugriff:** Lesen oder Schreiben einzelner Bits in Byte-, Word-, DWord- oder LWord-Variablen.       - Syntax: `MeineGanzzahlVar.%X<Bitnummer>` (IEC-konformer Zugriff, Bitnummer 0-n)       - Syntax (alternativ, systemabhängig): `MeineGanzzahlVar.<Bitnummer>` (z.B. `myWord.0`, `myWord.15`)     - **Bitstring-Literale:** Darstellung von Werten in Binärform (z.B. `myByte := 2#1100_0011;`).     - **Status-Flags:** Einzelne Bits innerhalb eines Bytes/Words werden oft als Flags verwendet, um verschiedene Zustände kompakt zu speichern.     - **Hardware-Abbildung:** Direkte Abbildung von digitalen Ein-/Ausgangssignalen auf Bits in Speicherbereichen (z.B. `%IX0.0`, `%QX0.1`).     - **Keine alleinstehende Variablendeklaration:** Man deklariert i.d.R. `VAR bFlag : BOOL; END_VAR` und nicht `VAR bFlag : BIT; END_VAR`.",
    syntaxExample: "VAR     byStatus : BYTE;     wConfig : WORD;     bEinzelFlag : BOOL; END_VAR",
    initializationExample: "byStatus.%X0 := TRUE;     (* Setzt das Bit 0 von byStatus auf 1 *) wConfig.3 := FALSE;      (* Setzt das Bit 3 von wConfig auf 0 (alternative Syntax) *) bEinzelFlag := byStatus.%X7; (* Liest den Wert von Bit 7 aus byStatus *)",
    image: "/assets/drawable/datatype_byte.svg"
  }
];

export const bacnetData: BacnetObjectInfo[] = [
  {
    name: "Accumulator (AC)",
    objectTypeIdentifier: "accumulator (23)",
    description: "Das Accumulator-Objekt dient zur Zählung von Impulsen, die einen physikalischen oder elektrischen Verbrauch repräsentieren (z.B. Energie, Volumen, Durchfluss). Es summiert diese Impulse über die Zeit auf und kann den aktuellen Zählerstand sowie die Rate der Zunahme bereitstellen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Accumulator-Objekt ist essenziell für Verbrauchsdatenerfassung.         - Der `Present_Value` wird typischerweise nicht direkt beschrieben, sondern erhöht sich durch interne oder externe Impulsquellen. Ein Schreiben auf `Present_Value` kann zum Zurücksetzen des Zählers verwendet werden.         - `Scale` und `Prescale` ermöglichen die Anpassung der Impulswertigkeit an die darzustellende Einheit.         - Beispiel: Ein Energiezähler liefert 100 Impulse pro kWh. `Units` wäre kWh. Wenn keine Skalierung gewünscht ist, wäre `Scale` = 1.0 und `Prescale` = 100 (oder `Scale` = 0.01 und `Prescale` = 1).         - Es kann Alarme generieren, wenn Grenzwerte überschritten werden oder der Zähler überläuft.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Analog Input (AI)",
    objectTypeIdentifier: "analog-input (0)",
    description: "Das Analog Input Objekt repräsentiert den Wert einer physikalischen Größe, die durch einen Sensor gemessen wird und einen kontinuierlichen Wertebereich hat (z.B. Temperatur, Druck, Helligkeit). Es liefert den aktuellen Wert dieser Messung.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Units",
    "Description"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "SubscribeCOV",
    "AcknowledgeAlarm",
    "GetAlarmSummary"
  ],
    detailedExplanation: "Der `Present_Value` ist der wichtigste Property und enthält den aktuellen Messwert. Die `Units` geben die Einheit des Messwerts an. Viele optionale Properties dienen der Alarmgenerierung (`High_Limit`, `Notification_Class` etc.) und der Konfiguration des Change of Value (COV) Reporting (`COV_Increment`). `Device_Type` kann eine textuelle Beschreibung des physikalischen Sensors sein.",
    image: "/assets/drawable/datatype_ai.svg"
  },
  {
    name: "Analog Output (AO)",
    objectTypeIdentifier: "analog-output (1)",
    description: "Das Analog Output Objekt repräsentiert einen Wert, der an einen Aktor gesendet wird, um eine physikalische Größe kontinuierlich zu steuern (z.B. Ventilposition, Heizleistung, Lichtdimmung). Der Wert kann durch ein BACnet-Gerät geschrieben werden.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Units",
    "Description"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "WriteProperty",
    "SubscribeCOV"
  ],
    detailedExplanation: "Der `Present_Value` kann über verschiedene Prioritäten (`Priority_Array`) geschrieben werden. Der `Relinquish_Default`-Wert wird aktiv, wenn keine höhere Priorität schreibt. Das `Feedback_Value` Property ist nützlich, wenn der Aktor eine tatsächliche Positions- oder Zustandsrückmeldung liefert.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Analog Value (AV)",
    objectTypeIdentifier: "analog-value (2)",
    description: "Das Analog Value Objekt repräsentiert einen analogen Wert, der nicht direkt von einem Sensor stammt oder einen Aktor steuert, sondern für interne Berechnungen, Sollwerte oder als Ergebnis einer Logik dient. Er kann sowohl gelesen als auch geschrieben werden.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Units",
    "Description"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "WriteProperty",
    "SubscribeCOV",
    "AcknowledgeAlarm",
    "GetAlarmSummary"
  ],
    detailedExplanation: "Flexibel einsetzbar für Sollwerte, berechnete Werte oder als Schnittstelle zu proprietären Systemen. Wenn als Sollwert beschreibbar, wird oft die `Priority_Array` verwendet. AV-Objekte können ebenfalls Alarme auslösen, ähnlich wie AI-Objekte.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Averaging (AVG)",
    objectTypeIdentifier: "averaging (19)",
    description: "Das Averaging-Objekt berechnet periodisch den Durchschnittswert einer referenzierten Property eines anderen BACnet-Objekts über ein definiertes Zeitintervall. Es wird häufig verwendet, um gleitende Mittelwerte von Messwerten wie Temperatur, Feuchtigkeit oder anderen analogen Größen zu ermitteln.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability",
    "Attempted_Samples",
    "Average_Value",
    "Buffer_Size",
    "Log_Buffer"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Averaging-Objekt ist nützlich, um stark schwankende Messwerte zu glätten oder Durchschnittsverbräuche über längere Zeiträume zu bilden.         - `Object_Property_Reference` ist entscheidend, da es festlegt, welcher Wert von welchem anderen Objekt für die Mittelwertbildung herangezogen wird.         - `Window_Interval` und `Buffer_Size` (oder `Window_Samples`) definieren, wie viele Werte in welchem Zeitraum in die Durchschnittsberechnung einfließen.         - Der `Present_Value` (oder `Average_Value`) gibt den errechneten Mittelwert aus.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Binary Input (BI)",
    objectTypeIdentifier: "binary-input (3)",
    description: "Das Binary Input Objekt repräsentiert den Zustand eines physikalischen Eingangs, der zwei Zustände annehmen kann (z.B. ein Schalter, ein Kontakt, ein Statussignal). Typische Zustände sind EIN/AUS, AKTIV/INAKTIV, ALARM/NORMAL.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "SubscribeCOV",
    "AcknowledgeAlarm",
    "GetAlarmSummary"
  ],
    detailedExplanation: "Binary Input-Objekte sind fundamental für die Erfassung digitaler Zustände.         - `Present_Value` gibt den aktuellen logischen Zustand zurück (oft als ENUMERATED: active/inactive).         - `Polarity` ist wichtig, um die physikalische Logik (Öffner/Schließer) korrekt auf die BACnet-Zustände abzubilden.         - `Active_Text` und `Inactive_Text` helfen bei der Visualisierung der Zustände (z.B. \"Geöffnet\" statt \"inactive\").         - Können Alarme auslösen, wenn sie in einen bestimmten `Alarm_Value`-Zustand wechseln.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Binary Output (BO)",
    objectTypeIdentifier: "binary-output (4)",
    description: "Das Binary Output Objekt repräsentiert einen Befehl an einen Aktor, der zwei Zustände annehmen kann (z.B. ein Relais schalten, ein Ventil öffnen/schließen). Der Zustand kann durch ein BACnet-Gerät geschrieben werden.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability",
    "Polarity"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "WriteProperty",
    "SubscribeCOV"
  ],
    detailedExplanation: "Binary Output-Objekte steuern digitale Aktoren.         - `Present_Value` kann über die `Priority_Array` mit verschiedenen Prioritäten beschrieben werden.         - `Relinquish_Default` bestimmt den Zustand, wenn keine Priorität aktiv ist.         - `Feedback_Value` ist nützlich, um eine Rückmeldung vom Feld zu erhalten (z.B. Endlagenschalter eines Ventils).         - `Minimum_On_Time` und `Minimum_Off_Time` können verwendet werden, um das Takten von Geräten zu verhindern.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Binary Value (BV)",
    objectTypeIdentifier: "binary-value (5)",
    description: "Das Binary Value Objekt repräsentiert einen binären Wert, der nicht direkt von einem physikalischen Ein- oder Ausgang stammt. Es dient für interne logische Zustände, Sollwerte oder als Ergebnis von Berechnungen. Es kann gelesen und geschrieben werden.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "WriteProperty",
    "SubscribeCOV",
    "AcknowledgeAlarm",
    "GetAlarmSummary"
  ],
    detailedExplanation: "Binary Value-Objekte sind vielseitig einsetzbar für interne Logik und Softwarepunkte.         - Wenn beschreibbar, wird oft die `Priority_Array` verwendet (z.B. für Sollwerte von verschiedenen Quellen).         - Können ebenfalls Alarme generieren, ähnlich wie BI-Objekte.         - Nützlich als Flags, Betriebsmodi oder als Schnittstelle zu nicht-BACnet-Systemen.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "BitString Value (BSV)",
    objectTypeIdentifier: "bitstring-value (39)",
    description: "Das BitString Value Objekt repräsentiert einen Wert, der als eine geordnete Folge von binären Bits (0 oder 1) dargestellt wird. Jedes Bit kann individuell adressiert und gesetzt oder gelesen werden. Es wird oft für Flags, Statusgruppen oder Binärdaten verwendet, die nicht durch einfache Binary Value-Objekte abgedeckt werden können.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das BitString Value Objekt ist nützlich, wenn mehrere binäre Zustände als eine Einheit behandelt werden sollen.         - `Present_Value` ist ein BACnetBITSTRING-Datentyp. Die Länge (Anzahl der Bits) wird entweder durch die `Bit_Mask` oder `Number_Of_Bits` bestimmt.         - `Bit_Mask` kann verwendet werden, um anzuzeigen, welche Bits innerhalb des `Present_Value` tatsächlich relevant sind oder eine Bedeutung haben.         - Beispiel: Eine Gruppe von 16 Alarm-Flags könnte als ein einzelnes BitString Value Objekt mit 16 Bits dargestellt werden.         - Der Zugriff auf einzelne Bits innerhalb des `Present_Value` erfolgt typischerweise über spezielle Mechanismen oder durch Lesen/Schreiben des gesamten Bitstrings und anschließender Bitmanipulation auf der Client-Seite.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Calendar",
    objectTypeIdentifier: "calendar (6)",
    description: "Das Calendar-Objekt definiert eine Liste von Kalenderdaten oder Datumsbereichen. Diese Liste wird typischerweise von anderen Objekten (wie Schedule-Objekten) referenziert, um festzulegen, ob ein bestimmter Tag als 'aktiv' oder 'besonders' für die Zeitplanung gilt (z.B. Feiertage, Ferien, spezielle Ereignistage).",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Calendar-Objekte sind entscheidend für die Erstellung flexibler Zeitpläne, die Ausnahmen wie Feiertage oder Betriebsferien berücksichtigen.         - Die `Date_List` ist das Kernstück und enthält eine Liste von Kalendereinträgen.         - Ein `BACnetCalendarEntry` kann ein einzelnes Datum, ein Datumsbereich (Start-/Enddatum), ein sich wiederholendes Muster (z.B. jeder Montag im Juli) oder ein Bitstring für Wochentage sein.         - Das `Present_Value` Property zeigt an, ob das aktuelle Datum des Geräts mit einem Eintrag in der `Date_List` übereinstimmt.         - Schedule-Objekte verwenden diese Information, um zu entscheiden, welcher Tagesplan an einem bestimmten Datum ausgeführt werden soll.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "CharacterString Value (CSV)",
    objectTypeIdentifier: "characterstring-value (40)",
    description: "Das CharacterString Value Objekt repräsentiert einen Wert, der als Zeichenkette (String) dargestellt wird. Es dient zur Speicherung und zum Austausch von Textinformationen, die nicht direkt von physikalischen Ein- oder Ausgängen stammen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das CharacterString Value Objekt ist ein vielseitiger Softwarepunkt für Textdaten.         - `Present_Value` ist vom Datentyp BACnetCharacterString.         - Es kann für Konfigurationsparameter, Beschreibungen, Meldungen, Benutzernamen oder jede andere Art von Textinformation verwendet werden.         - Wenn beschreibbar, kann die `Priority_Array` verwendet werden, um den Wert von verschiedenen Quellen zu steuern.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Command",
    objectTypeIdentifier: "command (7)",
    description: "Das Command-Objekt dient dazu, Aktionen in anderen BACnet-Objekten auszulösen oder Werte in diese zu schreiben, oft unter Berücksichtigung von Prioritäten und mit einer Bestätigung über den Erfolg der Aktion. Es gruppiert eine Liste von Aktionen, die auf andere Objekte ausgeführt werden können.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "In_Process",
    "Action"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Command-Objekt ist ein mächtiges Werkzeug zur Orchestrierung von Aktionen.         - Durch Schreiben eines Indexwertes auf `Present_Value` wird die entsprechende Aktion aus der `Action`-Liste ausgeführt.         - Jedes `BACnetActionCommand` in der `Action`-Liste spezifiziert:             - `Device_Identifier` und `Object_Identifier` des Zielobjekts.             - `Property_Identifier` des Ziel-Properties.             - `Property_Array_Index` (optional, falls das Property ein Array ist).             - `Property_Value` (der zu schreibende Wert).             - `Priority` (für das Schreiben auf priorisierte Properties).             - `Post_Delay` (optionale Verzögerung nach Ausführung der Aktion).             - `Quit_On_Failure` (ob die Sequenz bei einem Fehler abgebrochen werden soll).         - Nützlich für das Ausführen von Sequenzen von Befehlen, wie z.B. das Starten einer Anlage in mehreren Schritten.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "DatePattern Value (DPV)",
    objectTypeIdentifier: "proprietary/application-specific",
    description: "Das DatePattern Value Objekt dient zur Speicherung eines Datumsmusters. Dieses Muster könnte verwendet werden, um wiederkehrende Daten oder Bedingungen für Zeitpläne oder andere logische Operationen zu definieren (z.B. 'jeder erste Montag im Monat', 'alle Werktage'). Die genaue Struktur des Musters ist anwendungsspezifisch.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Dieses Objekt scheint zur Speicherung von spezifischen Datumsmustern zu dienen, die über einfache Datumslisten hinausgehen oder eine spezielle Kodierung verwenden.         - Die Interpretation des `Present_Value` hängt stark von der Implementierung ab. Es könnte ein String in einem definierten Format (z.B. cron-ähnlich) oder eine proprietäre Datenstruktur sein.         - In Standard-BACnet werden Datumsmuster typischerweise innerhalb des `Calendar`-Objekts über dessen `Date_List` Property und `BACnetCalendarEntry`-Strukturen gehandhabt, die verschiedene Mustertypen (einzelnes Datum, Datumsbereich, Wochentag/Monat-Kombinationen) unterstützen.         - Dieses Objekt könnte eine Vereinfachung oder Spezialisierung für bestimmte Anwendungsfälle darstellen.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "DateTimePattern Value (DTPV)",
    objectTypeIdentifier: "proprietary/application-specific",
    description: "Das DateTimePattern Value Objekt dient zur Speicherung eines Musters für Datum und Uhrzeit. Dieses Muster könnte verwendet werden, um wiederkehrende Zeitpunkte oder Bedingungen für Zeitpläne oder andere logische Operationen zu definieren (z.B. 'jeder Werktag um 08:00 Uhr', 'der erste Sonntag im Monat um Mitternacht'). Die genaue Struktur des Musters ist anwendungsspezifisch.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Dieses Objekt scheint zur Speicherung von spezifischen Datums- und Uhrzeitmustern zu dienen.         - Die Interpretation des `Present_Value` hängt stark von der Implementierung ab. Es könnte ein String in einem definierten Format (z.B. cron-ähnlich) oder eine proprietäre Datenstruktur sein, die sowohl Datums- als auch Zeitkomponenten eines Musters enthält.         - In Standard-BACnet werden solche Muster typischerweise innerhalb von `Schedule`-Objekten durch deren `Weekly_Schedule` und `Exception_Schedule` Properties gehandhabt, die auf `Calendar`-Objekte und spezifische Zeitangaben verweisen.         - Dieses Objekt könnte eine Vereinfachung oder Spezialisierung für bestimmte Anwendungsfälle darstellen, um komplexe Zeitmuster als einzelne Einheit zu speichern.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "DateTime Value (DTV)",
    objectTypeIdentifier: "datetime-value (43)",
    description: "Das DateTime Value Objekt repräsentiert einen spezifischen Zeitpunkt, bestehend aus einem Datum und einer Uhrzeit. Es dient zur Speicherung und zum Austausch von kompletten Zeitstempelinformationen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das DateTime Value Objekt ist ein Softwarepunkt für vollständige Zeitstempel.         - `Present_Value` ist vom Datentyp BACnetDateTime, welcher die Komponenten Jahr, Monat, Tag, Wochentag, Stunde, Minute, Sekunde und Hundertstelsekunde enthält.         - Es kann für Zeitstempel von Ereignissen, Gültigkeitsdaten, Start-/Endzeiten oder jeden anderen Zweck verwendet werden, der einen vollständigen Zeitpunkt erfordert.         - Wenn beschreibbar, kann die `Priority_Array` verwendet werden, um den Wert von verschiedenen Quellen zu steuern.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Date Value (DV)",
    objectTypeIdentifier: "date-value (42)",
    description: "Das Date Value Objekt repräsentiert ein spezifisches Datum. Es dient zur Speicherung und zum Austausch von reinen Datumsinformationen, ohne eine spezifische Uhrzeit.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Date Value Objekt ist ein Softwarepunkt für reine Datumswerte.         - `Present_Value` ist vom Datentyp BACnetDate, welcher die Komponenten Jahr, Monat, Tag und Wochentag enthält. Spezifische Werte wie 'ungerade', 'gerade' oder 'beliebig' sind für einige Felder möglich, um Muster darzustellen, wenn der Kontext es zulässt (obwohl dies eher in Calendar-Objekten verwendet wird).         - Es kann für Gültigkeitsdaten, Geburtstage, Start-/Enddaten ohne Zeitbezug oder ähnliche Zwecke verwendet werden.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Device Object",
    objectTypeIdentifier: "device (8)",
    description: "Das Device-Objekt repräsentiert ein BACnet-Gerät im Netzwerk. Es ist ein obligatorisches Objekt und fungiert als Container für alle anderen Objekte innerhalb des Geräts. Es stellt Informationen über das Gerät selbst bereit und verwaltet geräteweite Funktionen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "System_Status"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Device-Objekt ist das zentrale Element jedes BACnet-Geräts.         - Seine `Object_Identifier` (Geräte-Instanznummer) muss im BACnet-Internetwork eindeutig sein.         - Die `Object_List` ist entscheidend, da sie alle anderen Objekte im Gerät auflistet und somit die \"Navigation\" zu diesen Objekten ermöglicht.         - Properties wie `Vendor_Name`, `Model_Name` und `Firmware_Revision` geben wichtige Informationen über das Gerät selbst.         - `Protocol_Services_Supported` und `Protocol_Object_Types_Supported` informieren andere Geräte darüber, welche BACnet-Funktionen dieses Gerät beherrscht.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Event Enrollment (EE)",
    objectTypeIdentifier: "event-enrollment (9)",
    description: "Das Event Enrollment-Objekt (auch Ereignis-Registrierung) dient zur Definition von Kriterien für die Generierung von Ereignisbenachrichtigungen (Events/Alarme). Es überwacht den Wert oder Zustand eines referenzierten Properties eines anderen Objekts und löst ein Event aus, wenn bestimmte Bedingungen erfüllt sind (z.B. Grenzwertüberschreitung, Zustandsänderung).",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Description",
    "Event_State"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Event Enrollment-Objekte sind zentral für das Alarmmanagement in BACnet.         - Sie entkoppeln die Ereignislogik vom eigentlichen Datenpunkt (z.B. ein AI-Objekt). Ein AI-Objekt liefert nur einen Wert, das EE-Objekt entscheidet, ob dieser Wert einen Alarm auslöst.         - `Event_Parameters` ist ein komplexer Datentyp, dessen Inhalt vom gewählten `Event_Type` abhängt. Für `OUT_OF_RANGE` enthält es z.B. `Time_Delay`, `Low_Limit`, `High_Limit`, `Deadband`.         - Die `Notification_Class` bestimmt, wer bei einem Ereignis benachrichtigt wird und auf welchem Weg.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Integer Value (IV)",
    objectTypeIdentifier: "integer-value (44)",
    description: "Das Integer Value Objekt repräsentiert einen ganzzahligen Wert mit Vorzeichen. Es dient zur Speicherung und zum Austausch von numerischen Informationen, die keine Nachkommastellen haben und nicht direkt von physikalischen Ein- oder Ausgängen stammen (ähnlich dem Analog Value für Gleitkommazahlen oder Binary Value für Binärzustände).",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Integer Value Objekt ist ein Softwarepunkt für ganzzahlige Werte.         - `Present_Value` ist vom Datentyp BACnetSignedInteger, was typischerweise einem 32-Bit-Integer entspricht.         - Es kann für Zähler, Sollwerte, Konfigurationsparameter oder jede andere Art von ganzzahliger Information verwendet werden.         - Wenn beschreibbar, kann die `Priority_Array` verwendet werden, um den Wert von verschiedenen Quellen zu steuern.         - Es unterscheidet sich von `Analog Value` dadurch, dass es speziell für Ganzzahlen gedacht ist, während `Analog Value` für Gleitkommazahlen (REAL) vorgesehen ist.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Large Analog Value (LAV)",
    objectTypeIdentifier: "large-analog-value (45)",
    description: "Das Large Analog Value Objekt repräsentiert einen Gleitkommawert mit doppelter Genauigkeit (Double Precision / 64-Bit Real). Es ist vergleichbar mit dem Analog Value Objekt, bietet aber einen größeren Wertebereich und höhere Präzision. Es wird für numerische Werte verwendet, die nicht direkt von physikalischen Ein- oder Ausgängen stammen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Large Analog Value Objekt ist für Anwendungen gedacht, die eine höhere Präzision oder einen größeren Wertebereich erfordern, als das normale Analog Value Objekt (das REAL / 32-Bit Float verwendet) bieten kann.         - `Present_Value` ist vom Datentyp BACnetDouble.         - Typische Anwendungsfälle sind hochpräzise Berechnungen, sehr große oder sehr kleine Messwerte oder Werte aus wissenschaftlichen Anwendungen.         - Wenn beschreibbar, kann die `Priority_Array` verwendet werden.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Load Control (LC)",
    objectTypeIdentifier: "load-control (28)",
    description: "Das Load Control-Objekt (Laststeuerung) dient zur Verwaltung und Steuerung des Energieverbrauchs oder anderer Lasten. Es kann Lasten basierend auf verschiedenen Kriterien abwerfen (shed) oder wieder zuschalten (permit), z.B. bei Erreichen eines Leistungsscheitels, auf Basis eines externen Signals oder eines Zeitplans.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Load Control-Objekte sind wichtig für Energieoptimierung und Lastmanagement.         - Sie ermöglichen es, kontrolliert Verbraucher ab- und zuzuschalten, um Spitzenlasten zu kappen oder auf Preissignale zu reagieren.         - `Shed_Levels` definiert, welche Lasten bei welchem Abwurfniveau betroffen sind. Ein `BACnetShedLevel` kann den abzuwerfenden Leistungsanteil oder eine Liste von zu steuernden Objekten (z.B. Binary Outputs) enthalten.         - Das Objekt kann auf Anforderung (`Requested_Shed_Level`), zeitgesteuert (`Start_Time`, `Shed_Duration`) oder durch interne Logik (z.B. Überwachung eines Leistungswerts) agieren.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Loop",
    objectTypeIdentifier: "loop (12)",
    description: "Das Loop-Objekt repräsentiert eine Regelungsschleife (z.B. PID-Regler). Es berechnet einen Ausgangswert (Stellgröße) basierend auf einem Sollwert, einem Istwert (Prozessvariable) und Regelparametern, um den Istwert möglichst nah am Sollwert zu halten.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Loop-Objekte sind das Herzstück vieler Regelungsaufgaben in der Gebäudeautomation.         - Sie implementieren typischerweise PID-Regelalgorithmen (Proportional, Integral, Differential).         - `Setpoint_Reference` und `Controlled_Variable_Reference` verknüpfen den Regler mit den relevanten Datenpunkten (Soll- und Istwert).         - Die `Action` (direct/reverse) bestimmt, ob der Reglerausgang bei steigender Regelabweichung steigt oder fällt.         - Die PID-Parameter (`Proportional_Constant`, `Integral_Constant`, `Derivative_Constant`) müssen sorgfältig auf die Regelstrecke abgestimmt werden.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Notification Class (NOC)",
    objectTypeIdentifier: "notification-class (15)",
    description: "Das Notification Class-Objekt definiert, wie und wohin Ereignis- und Alarmbenachrichtigungen (Notifications) gesendet werden. Es gruppiert Empfängerlisten und Konfigurationsparameter für verschiedene Ereignistypen und Prioritäten.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Description",
    "Notification_Class",
    "Priority"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Notification Class-Objekte sind zentral für das Alarmmanagement und die Verteilung von Ereignismeldungen.         - Event-generierende Objekte (wie AI, BI, EE) referenzieren eine `Notification_Class`, um zu bestimmen, was bei einem Event passiert.         - Die `Recipient_List` enthält `BACnetDestination`-Einträge. Jeder Eintrag definiert einen Empfänger (Gerät oder Adresse), einen Zeitplan für die Gültigkeit der Benachrichtigung an diesen Empfänger, und welche Übergänge an diesen Empfänger gesendet werden sollen.         - `Ack_Required` legt fest, ob Benutzer die Alarme quittieren müssen.         - Es kann mehrere Notification Class-Objekte in einem Gerät geben, um unterschiedliche Alarmierungsstrategien zu ermöglichen.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "OctetString Value (OSV)",
    objectTypeIdentifier: "octetstring-value (46)",
    description: "Das OctetString Value Objekt repräsentiert einen Wert, der als eine Folge von Oktetten (Bytes) dargestellt wird. Es dient zur Speicherung und zum Austausch von beliebigen Binärdaten, deren Struktur nicht durch andere BACnet-Datentypen abgedeckt wird.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das OctetString Value Objekt ist ein flexibler Softwarepunkt für generische Binärdaten.         - `Present_Value` ist vom Datentyp BACnetOctetString.         - Es kann für proprietäre Datenformate, verschlüsselte Daten, Konfigurationsblobs oder jede andere Art von Byte-Sequenz verwendet werden, bei der die interne Struktur für BACnet nicht relevant ist, sondern nur für die sendende und empfangende Applikation.         - Wenn beschreibbar, kann die `Priority_Array` verwendet werden.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Positive Integer Value (PIV)",
    objectTypeIdentifier: "positive-integer-value (48)",
    description: "Das Positive Integer Value Objekt repräsentiert einen ganzzahligen Wert ohne Vorzeichen (Unsigned Integer). Es dient zur Speicherung und zum Austausch von numerischen Informationen, die keine Nachkommastellen haben, nicht negativ sind und nicht direkt von physikalischen Ein- oder Ausgängen stammen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Positive Integer Value Objekt ist ein Softwarepunkt für nicht-negative Ganzzahlen.         - `Present_Value` ist vom Datentyp BACnetUnsignedInteger, was typischerweise einem 32-Bit Unsigned Integer entspricht (0 bis 4.294.967.295).         - Es kann für Zähler, die nur positiv sind, Größenangaben, IDs oder jede andere Art von nicht-negativer ganzzahliger Information verwendet werden.         - Wenn beschreibbar, kann die `Priority_Array` verwendet werden.         - Es unterscheidet sich von `Integer Value` dadurch, dass es keine negativen Werte zulässt.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Multi-state Input (MSI)",
    objectTypeIdentifier: "multi-state-input (13)",
    description: "Das Multi-state Input Objekt repräsentiert den Zustand eines physikalischen Eingangs, der eine definierte Anzahl von diskreten Zuständen annehmen kann (mehr als zwei). Beispiele sind mehrstufige Schalter, Betriebsmoduswahlschalter oder Sensoren mit mehreren definierten Zuständen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "SubscribeCOV",
    "AcknowledgeAlarm",
    "GetAlarmSummary"
  ],
    detailedExplanation: "Multi-state Input-Objekte dienen zur Erfassung von Eingängen mit mehreren definierten Zuständen.         - `Present_Value` ist ein positiver Integer, der den aktuellen Zustand repräsentiert. Der Wert 1 entspricht dem ersten Zustand, 2 dem zweiten usw., bis zum Wert von `Number_Of_States`.         - `Number_Of_States` muss bei der Konfiguration des Objekts festgelegt werden.         - `State_Text` ist ein Array von Zeichenketten, das jedem Integer-Wert von `Present_Value` eine menschenlesbare Bedeutung zuweist.         - Kann Alarme auslösen, wenn `Present_Value` einem der Werte in `Alarm_Values` oder `Fault_Values` entspricht.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Multi-state Output (MSO)",
    objectTypeIdentifier: "multi-state-output (14)",
    description: "Das Multi-state Output Objekt repräsentiert einen Befehl an einen Aktor, der eine definierte Anzahl von diskreten Zuständen annehmen kann (mehr als zwei). Beispiele sind mehrstufige Pumpen, Lüfter oder Klappen mit mehreren festen Positionen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability",
    "Number_Of_States"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "WriteProperty",
    "SubscribeCOV"
  ],
    detailedExplanation: "Multi-state Output-Objekte steuern Aktoren mit mehreren diskreten Stellungen oder Stufen.         - `Present_Value` ist ein positiver Integer (1 bis `Number_Of_States`), der über die `Priority_Array` geschrieben werden kann.         - `Relinquish_Default` bestimmt den Zustand, wenn keine Priorität aktiv ist.         - `Feedback_Value` kann verwendet werden, um eine Rückmeldung vom Feld über den tatsächlich eingestellten Zustand zu erhalten.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Multi-state Value (MSV)",
    objectTypeIdentifier: "multi-state-value (19)",
    description: "Das Multi-state Value Objekt repräsentiert einen Wert, der eine definierte Anzahl von diskreten Zuständen annehmen kann und nicht direkt von einem physikalischen Ein- oder Ausgang stammt. Es dient für interne logische Zustände, Sollwerte oder als Ergebnis von Berechnungen mit mehreren Zuständen.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability",
    "Number_Of_States"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple",
    "WriteProperty",
    "SubscribeCOV",
    "AcknowledgeAlarm",
    "GetAlarmSummary"
  ],
    detailedExplanation: "Multi-state Value-Objekte sind vielseitig für interne Softwarepunkte mit mehreren Zuständen.         - `Present_Value` ist ein positiver Integer (1 bis `Number_Of_States`).         - Wenn beschreibbar, wird oft die `Priority_Array` verwendet (z.B. für Sollwerte von verschiedenen Quellen).         - Nützlich für Betriebsmodi, Statusanzeigen mit mehr als zwei Zuständen oder als Schnittstelle zu nicht-BACnet-Systemen.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Program",
    objectTypeIdentifier: "program (20)",
    description: "Das Program-Objekt dient zur Steuerung und Überwachung von Programmen oder Anwendungen innerhalb eines BACnet-Geräts. Es ermöglicht das Starten, Stoppen, Laden oder Entladen von Programmen und gibt Auskunft über deren aktuellen Zustand.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Program_State"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Program-Objekte ermöglichen die Verwaltung von eingebetteten Anwendungen oder Logiksequenzen in BACnet-Geräten.         - Durch Schreiben auf das `Program_Change`-Property kann der Lebenszyklus des Programms gesteuert werden.         - `Program_State` gibt Auskunft über den aktuellen Ausführungszustand.         - `Reason_For_Halt` und `Description_Of_Halt` sind nützlich für die Diagnose, falls ein Programm unerwartet stoppt.         - Sie werden oft für komplexe Steuerungslogik, Sequenzen oder anwenderspezifische Algorithmen verwendet, die über die Standardfunktionalität anderer BACnet-Objekte hinausgehen.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Pulse Converter (PC)",
    objectTypeIdentifier: "pulse-converter (24)",
    description: "Das Pulse Converter-Objekt wandelt eine Folge von Impulsen (typischerweise von einem Zähler oder Sensor) in einen für Menschen lesbaren numerischen Wert um, der einen Verbrauch oder eine Menge repräsentiert. Es berücksichtigt dabei Skalierungsfaktoren, Einheiten und kann auch Zählerüberläufe handhaben.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Status_Flags",
    "Event_State",
    "Out_Of_Service",
    "Reliability",
    "Units"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Das Pulse Converter-Objekt dient zur Aufbereitung von Impulsdaten für die Anzeige oder Weiterverarbeitung.         - Es nimmt rohe Impulszählungen (oft von einem Accumulator-Objekt via `Input_Reference` oder intern gezählt) und wandelt sie mittels `Scale_Factor` und optional `Adjust_Value` in einen bedeutungsvollen Wert um.         - Beispiel: Ein Gaszähler liefert Impulse. Der Pulse Converter skaliert diese Impulse in Kubikmeter (m³) um.         - Das `Count`-Property kann oft zurückgesetzt werden, während `Present_Value` den kumulierten, skalierten Wert darstellt.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Schedule",
    objectTypeIdentifier: "schedule (17)",
    description: "Das Schedule-Objekt definiert einen Zeitplan, der bestimmt, welcher Wert zu welcher Zeit an ein oder mehrere referenzierte Properties anderer Objekte geschrieben werden soll. Es unterstützt Wochenpläne und Ausnahmetage (über Verweis auf Calendar-Objekte).",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Present_Value",
    "Description",
    "Effective_Period"
  ],
    typicalServices: [
    "ReadProperty",
    "ReadPropertyMultiple"
  ],
    detailedExplanation: "Schedule-Objekte sind fundamental für die zeitbasierte Steuerung in BACnet.         - `Weekly_Schedule`: Definiert für jeden Wochentag eine Liste von Uhrzeiten und den zugehörigen Werten, die aktiv werden sollen.         - `Exception_Schedule`: Ermöglicht die Definition abweichender Zeitpläne für spezielle Tage (z.B. Feiertage, Ferien), die über `Calendar`-Objekte identifiziert werden.         - `List_Of_Object_Property_References`: Verknüpft den Zeitplan mit den zu steuernden Datenpunkten (z.B. der Sollwert eines Loop-Objekts, der `Present_Value` eines Binary Output).         - Der `Present_Value` des Schedule-Objekts ändert sich automatisch gemäß den definierten Zeiten und wird dann auf die referenzierten Properties geschrieben.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Trend Log (TL)",
    objectTypeIdentifier: "trend-log (20)",
    description: "Das Trend Log-Objekt dient zur Aufzeichnung von Datenwerten über die Zeit. Es sammelt periodisch oder bei Wertänderung (COV) den Wert eines referenzierten Properties eines anderen BACnet-Objekts und speichert diese Werte mit Zeitstempeln in einem Puffer.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Description",
    "Enable",
    "Start_Time",
    "Stop_Time",
    "Log_DeviceObjectProperty",
    "Log_Interval",
    "Stop_When_Full"
  ],
    typicalServices: [],
    detailedExplanation: "Trend Log-Objekte sind essenziell für die Datenhistorisierung und Analyse.         - Sie können Daten entweder periodisch (`Logging_Type = POLLED` mit `Log_Interval`) oder bei Wertänderung (`Logging_Type = COV`) aufzeichnen.         - Der `Log_Buffer` speichert `BACnetLogRecord`-Einträge, die typischerweise einen Zeitstempel, den aufgezeichneten Wert und dessen Status enthalten.         - Mit `AtomicReadFile` kann der Inhalt des `Log_Buffer` effizient ausgelesen werden.         - `Stop_When_Full` bestimmt das Verhalten, wenn der Puffer voll ist (stoppen oder als Ringpuffer weiterlaufen).",
    image: "/assets/drawable/bacnet_symbol.jpg"
  },
  {
    name: "Trend Log Multiple (TLM)",
    objectTypeIdentifier: "trend-log-multiple (27)",
    description: "Das Trend Log Multiple-Objekt ist eine Erweiterung des Trend Log-Objekts und ermöglicht die Aufzeichnung der Werte von mehreren Properties (von verschiedenen Objekten) gleichzeitig in einem einzigen Log. Jeder Datensatz im Log enthält dann die Werte aller spezifizierten Properties zum jeweiligen Zeitstempel.",
    commonProperties: [
    "Object_Identifier",
    "Object_Name",
    "Object_Type",
    "Description",
    "Enable",
    "Start_Time",
    "Stop_Time",
    "Log_Interval",
    "Stop_When_Full",
    "Buffer_Size",
    "Log_Buffer",
    "Record_Count",
    "Total_Record_Count",
    "Logging_Type"
  ],
    typicalServices: [],
    detailedExplanation: "Trend Log Multiple-Objekte bieten eine effizientere Möglichkeit, zusammengehörige Datenpunkte zu historisieren.         - Der Hauptunterschied zum einfachen Trend Log ist das `Log_Specs`-Property. Es ist eine Liste, die auf mehrere Properties verweist, die gemeinsam aufgezeichnet werden sollen.         - Jeder Eintrag im `Log_Buffer` enthält dann einen Zeitstempel und die Werte aller in `Log_Specs` definierten Properties zu diesem Zeitpunkt.         - Dies ist nützlich, um z.B. Sollwert, Istwert und Stellgröße eines Reglers oder mehrere Umgebungsparameter (Temperatur, Feuchte, CO2) synchron aufzuzeichnen.         - Die Funktionsweise bezüglich Aufzeichnungsarten (POLLED, COV), Pufferhandling etc. ist ansonsten sehr ähnlich zum einzelnen Trend Log-Objekt.",
    image: "/assets/drawable/bacnet_symbol.jpg"
  }
];





